import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../components/PageLayout";
import Loader from "../components/Loader";
import { branddata } from "../components/chaseListComponents/chaseListData";
import axiosInstance from "../utils/axiosInstance";
import { ParseErrors } from "../utils/ParseErrors";

type Sms = { label: string; text: string };
type Preview = { key: string; badge: string; subject: string; html: string; sms: Sms[] };
type PreviewGroup = { key: string; title: string; blurb: string; meta: string; items: Preview[] };

const TEMPLATE_BRANDS = branddata.filter((brand) => !brand.name.includes("-"));

// ----------------------- EMAIL IN A SANDBOXED FRAME ------------------------------------
const fitFrame = (frame: HTMLIFrameElement) => {
  const fit = () =>
    frame.contentDocument && (frame.style.height = `${frame.contentDocument.documentElement.scrollHeight}px`);
  fit();
  setTimeout(fit, 400);
};

const EmailFrame = ({ html }: { html: string }) => (
  <iframe
    title="email preview"
    srcDoc={html}
    sandbox="allow-same-origin"
    scrolling="no"
    className="w-full border-0 block"
    onLoad={(e) => fitFrame(e.currentTarget)}
  />
);

const Label = ({ text }: { text: string }) => (
  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-light-gray mb-2">{text}</p>
);

// ----------------------- ONE EMAIL AND ITS SMS ------------------------------------
const TouchpointCard = ({ item }: { item: Preview }) => (
  <div className="rounded-2xl border border-edge bg-surface-elevated p-5">
    <div className="flex flex-wrap items-center gap-3 mb-5">
      <span className="rounded-full bg-accent px-3.5 py-1.5 text-xs font-bold tracking-wide text-white">
        {item.badge}
      </span>
      {item.sms.length > 0 && <span className="text-sm text-light-gray">+ SMS</span>}
    </div>

    <div className="flex gap-6 desktop:flex-col">
      <div className="min-w-0 flex-1">
        <Label text="Email" />
        <p className="text-[15px] text-text-primary mb-4">
          <span className="font-bold">Subject: </span>
          {item.subject}
        </p>
        <div className="max-h-[560px] overflow-y-auto rounded-xl border border-edge">
          <EmailFrame html={item.html} />
        </div>
      </div>

      {item.sms.length > 0 && (
        <div className="w-[340px] shrink-0 desktop:w-full flex flex-col gap-4">
          {item.sms.map((sms) => (
            <div key={sms.label}>
              <Label text={sms.label} />
              <div className="rounded-2xl border border-edge bg-surface p-4">
                <p className="rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 px-4 py-3 text-[15px] leading-relaxed text-white">
                  {sms.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const MessageTemplates = () => {
  const [value, setValue] = useState({ brand: TEMPLATE_BRANDS[0].name, open: "" });
  const [groups, setGroups] = useState<PreviewGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const set = (patch: Partial<typeof value>) => setValue((s) => ({ ...s, ...patch }));

  // ----------------------- LOAD PREVIEWS FOR THE BRAND ------------------------------------
  useEffect(() => {
    const findPreviews = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/templates/preview?brand=${value.brand}`);
        setGroups(res.data);
      } catch (error) {
        toast.error(ParseErrors(error));
      }
      setLoading(false);
    };

    findPreviews();
  }, [value.brand]);

  return (
    <PageLayout pageTitle="Message Templates">
      <p className="max-w-3xl text-[15px] leading-relaxed text-light-gray">
        Every chase email and SMS, rendered as it sends with a sample customer. Pick a brand, then open a group.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        {TEMPLATE_BRANDS.map((brand) => (
          <button
            key={brand.name}
            type="button"
            onClick={() => set({ brand: brand.name, open: "" })}
            className={clsx(
              "rounded-full px-6 py-2.5 text-[15px] font-semibold transition-all duration-200",
              value.brand === brand.name
                ? "bg-accent text-white"
                : "border border-edge bg-surface text-text-primary hover:border-accent/40",
            )}
          >
            {brand.name === "BizC" ? "Business Compare" : brand.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10">
          <Loader />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-4">
          {groups.map((group) => {
            const open = value.open === group.key;
            return (
              <div key={group.key} className="rounded-2xl border border-edge bg-surface overflow-hidden">
                <button
                  type="button"
                  onClick={() => set({ open: open ? "" : group.key })}
                  className="flex w-full items-center gap-4 px-6 py-5 text-left hover:bg-active-background/50 transition-colors duration-200"
                >
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className={clsx("text-light-gray transition-transform duration-300", open && "rotate-90")}
                  />
                  <span className="text-[17px] font-bold text-text-primary">{group.title}</span>
                  <span className="ml-auto text-sm text-light-gray">{group.meta}</span>
                </button>

                {open && (
                  <div className="border-t border-edge-faint px-6 pb-6 pt-5">
                    <p className="mb-5 text-[15px] text-light-gray">{group.blurb}</p>
                    <div className="flex flex-col gap-5">
                      {group.items.map((item) => (
                        <TouchpointCard key={item.key} item={item} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </PageLayout>
  );
};

export default MessageTemplates;
